import { Button, Col, Input, Row, Segmented, Space, Spin, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchCallDetail, fetchRecordingBlob } from "../../../apis/call_intelligence.api";

const { Text, Paragraph } = Typography;

export const SENTIMENT_COLOR: Record<string, string> = {
  frustrated: "red",
  angry: "volcano",
  neutral_low: "gold",
  neutral: "blue",
  satisfied: "green",
};
export const SENTIMENT_WORD: Record<string, string> = {
  frustrated: "frustrated",
  angry: "angry",
  neutral_low: "neutral low",
  neutral: "neutral",
  satisfied: "satisfied",
};

export const ORDER_STATUS: Record<string, string> = {
  order_placed: "Placed",
  order_confirmed: "Confirmed",
  partly_shipped: "Partly shipped",
  shipped: "Shipped",
  partly_delivered: "Partly delivered",
  delivered: "Delivered",
  order_cancelled: "Cancelled",
  refunded: "Refunded",
};
export const orderStatus = (s?: string) => (s ? ORDER_STATUS[s] || s.replace(/_/g, " ") : "-");

export const inr = (n?: number) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

// "45 min", "3 h 12 min", "2 d 4 h"
export const mins = (m: number) => {
  if (m < 60) return `${Math.round(m)} min`;
  if (m < 1440) return `${Math.floor(m / 60)} h ${Math.round(m % 60)} min`;
  return `${Math.floor(m / 1440)} d ${Math.floor((m % 1440) / 60)} h`;
};
export const minsSince = (iso?: string | null) => (iso ? dayjs().diff(dayjs(iso), "minute", true) : 0);
export const ago = (iso?: string | null) => (iso ? `${mins(Math.max(0, minsSince(iso)))} ago` : "");
export const fmtDT = (iso?: string | null) => (iso ? dayjs(iso).format("D MMM, h:mm a") : "-");
export const fmtD = (iso?: string | null) => (iso ? dayjs(iso).format("D MMM") : "-");
export const dur = (s?: number | null) =>
  s == null ? "-" : s >= 60 ? `${Math.floor(s / 60)} min ${s % 60} s` : `${s} s`;
export const direction = (callType?: string) => (callType === "incoming" ? "Inbound" : "Outbound");

export const sentimentTag = (label?: string, severity?: string) =>
  label ? (
    <span>
      <Tag color={SENTIMENT_COLOR[label] || "default"} style={{ marginRight: 4 }}>
        {SENTIMENT_WORD[label] || label}
      </Tag>
      {severity === "high" && <Text type="secondary">high</Text>}
    </span>
  ) : (
    <Tag>no call linked</Tag>
  );

export const flagReason = (r: any) => {
  const sentiment = SENTIMENT_WORD[r.latest_conversation_sentiment] || r.latest_conversation_sentiment;
  const update =
    r.minutes_since_call_update != null
      ? `No call update for ${mins(r.minutes_since_call_update)}.`
      : "No call update on record.";
  return `Last conversation (call, ${ago(r.latest_conversation_at)}) was ${sentiment}. ${update}`;
};

export const FlagTag = ({ flagged, sentiment }: { flagged: boolean; sentiment?: string }) =>
  flagged ? <Tag color={sentiment === "angry" ? "volcano" : "red"}>Flagged</Tag> : <Tag>Not flagged</Tag>;

export const QC_FLAG_COLOR: Record<string, string> = { red: "red", yellow: "orange", green: "green" };

// Audio is fetched through our backend (Kylas links expire in minutes and
// MyOperator's need a panel login, so neither can play in-page directly).
const useRecording = (callId?: number, moLogId?: number) => {
  const [src, setSrc] = useState<string>();
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  const pending = useRef<Promise<void>>();

  useEffect(
    () => () => {
      if (src) URL.revokeObjectURL(src);
    },
    [src]
  );

  const load = () => {
    if (src) return Promise.resolve();
    if (!pending.current) {
      setState("loading");
      pending.current = fetchRecordingBlob(callId ? "call" : "myoperator", (callId || moLogId) as number)
        .then((blob) => {
          setSrc(URL.createObjectURL(blob.type.startsWith("audio/") ? blob : new Blob([blob], { type: "audio/mpeg" })));
          setState("idle");
        })
        .catch(() => {
          setState("error");
          pending.current = undefined;
          throw new Error("recording unavailable");
        });
    }
    return pending.current;
  };
  return { src, state, load };
};

const RecordingError = ({ fallbackUrl }: { fallbackUrl?: string }) => (
  <Text type="warning" style={{ fontSize: 12 }}>
    Recording not available.{" "}
    {fallbackUrl && (
      <a href={fallbackUrl} target="_blank" rel="noreferrer">
        Open in MyOperator ↗ (needs MyOperator login)
      </a>
    )}
  </Text>
);

// Compact player for table rows.
export const RecordingPlayer = ({
  callId,
  moLogId,
  fallbackUrl,
}: {
  callId?: number;
  moLogId?: number;
  fallbackUrl?: string;
}) => {
  const { src, state, load } = useRecording(callId, moLogId);
  if (!callId && !moLogId) return null;
  return (
    <div style={{ margin: "6px 0" }}>
      {src ? (
        <audio controls autoPlay src={src} style={{ width: "100%", height: 36 }} />
      ) : (
        <Space wrap>
          <Button size="small" loading={state === "loading"} onClick={() => load().catch(() => undefined)}>
            ▶ Play
          </Button>
          {state === "error" && <RecordingError fallbackUrl={fallbackUrl} />}
        </Space>
      )}
    </div>
  );
};

// "01:02" / "1:02:03" → seconds
export const toSec = (t?: string) => (t || "").split(":").reduce((acc, part) => acc * 60 + (Number(part) || 0), 0);

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^\p{L}\p{N} ]/gu, "")
    .replace(/\s+/g, " ")
    .trim();

// Index of the transcript line a QC evidence quote came from, or -1.
const findQuote = (lines: any[], quote: string) => {
  const q = norm(quote);
  if (!q) return -1;
  const probe = q.slice(0, 40);
  const i = lines.findIndex((l) => norm(l.text).includes(probe));
  return i >= 0 ? i : lines.findIndex((l) => norm(l.text).length > 8 && q.includes(norm(l.text)));
};

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const Highlighted = ({ text, search, keywords }: { text: string; search: string; keywords: string[] }) => {
  const terms = [search.trim(), ...keywords].filter((t) => t.length > 1);
  if (!terms.length) return <>{text}</>;
  const re = new RegExp(`(${terms.map(escapeRe).join("|")})`, "gi");
  const kw = keywords.map((k) => k.toLowerCase());
  return (
    <>
      {text.split(re).map((part, i) =>
        i % 2 === 0 ? (
          part
        ) : (
          <mark
            key={i}
            style={
              kw.includes(part.toLowerCase())
                ? { background: "#ffccc7", padding: 0, fontWeight: 600 }
                : { background: "#fff566", padding: 0 }
            }
          >
            {part}
          </mark>
        )
      )}
    </>
  );
};

const SIDE_STYLE = [
  { justify: "flex-start", bg: "#e6f4ff", border: "#91caff", name: "#0958d9", bar: "#4096ff" },
  { justify: "flex-end", bg: "#f6ffed", border: "#b7eb8f", name: "#389e0d", bar: "#73d13d" },
];

const speakerSides = (lines: any[]) => {
  const known = lines.some((l) => l.speaker === "Agent" || l.speaker === "Customer");
  const order: string[] = [];
  lines.forEach((l) => !order.includes(l.speaker) && order.push(l.speaker));
  const sideOf = (speaker: string) => (known ? (speaker === "Customer" ? 1 : 0) : Math.min(order.indexOf(speaker), 1));
  return {
    known,
    sideOf,
    legend: known ? ["Agent", "Customer"] : order.slice(0, 2),
  };
};

// Share of speaking time per side, from turn start times (the last turn runs
// to the end of the call, or is estimated from its length).
const talkShare = (lines: any[], sideOf: (s: string) => number, duration?: number | null) => {
  const secs = [0, 0];
  lines.forEach((l, i) => {
    const start = toSec(l.time);
    const end =
      i + 1 < lines.length ? toSec(lines[i + 1].time) : Math.max(start, duration || start + l.text.length / 15);
    secs[sideOf(l.speaker)] += Math.max(0, end - start);
  });
  const total = secs[0] + secs[1];
  return total ? secs.map((s) => Math.round((s / total) * 100)) : null;
};

const TalkRatio = ({ share, legend, known }: { share: number[]; legend: string[]; known: boolean }) => (
  <div style={{ fontSize: 12 }}>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
      <span style={{ color: SIDE_STYLE[0].name }}>
        {legend[0]} talks {share[0]}%
      </span>
      {legend[1] && (
        <span style={{ color: SIDE_STYLE[1].name }}>
          {legend[1]} {share[1]}%
        </span>
      )}
    </div>
    <div style={{ display: "flex", height: 6, borderRadius: 3, overflow: "hidden", background: "#f0f0f0" }}>
      <div style={{ width: `${share[0]}%`, background: SIDE_STYLE[0].bar }} />
      <div style={{ width: `${share[1]}%`, background: SIDE_STYLE[1].bar }} />
    </div>
    {!known && <Text type="secondary">Couldn't tell which speaker is the agent</Text>}
  </div>
);

const SPEEDS = [1, 1.25, 1.5, 2];

// A quoted line to scroll to, or a "mm:ss" time to play from.
export type PlaybackFocus = { quote?: string; time?: string; at: number };

// Recording + two-sided chat transcript, synced: click a line's time to play
// from there, the line being spoken is highlighted, search and flagged
// keywords are marked. `focus` scrolls to (and plays from) a quoted line.
export const CallPlayback = ({
  callId,
  fallbackUrl,
  lines,
  raw,
  duration,
  keywords = [],
  focus,
  maxHeight = 460,
}: {
  callId?: number;
  fallbackUrl?: string;
  lines?: any[];
  raw?: string;
  duration?: number | null;
  keywords?: string[];
  focus?: PlaybackFocus;
  maxHeight?: number;
}) => {
  const { src, state, load } = useRecording(callId);
  const audio = useRef<HTMLAudioElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const seekTo = useRef<number | null>(null);
  const [speed, setSpeed] = useState(1);
  const [now, setNow] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [search, setSearch] = useState("");
  const [flash, setFlash] = useState(-1);

  const turns = useMemo(() => lines || [], [lines]);
  const starts = useMemo(() => turns.map((l) => toSec(l.time)), [turns]);
  const sides = useMemo(() => speakerSides(turns), [turns]);
  const share = useMemo(() => talkShare(turns, sides.sideOf, duration), [turns, sides, duration]);
  const matches = useMemo(
    () => (search.trim().length > 1 ? turns.filter((l) => norm(l.text).includes(norm(search))).length : 0),
    [turns, search]
  );

  let active = -1;
  if (now >= 0) for (let i = 0; i < starts.length && starts[i] <= now + 0.25; i++) active = i;

  const scrollToLine = (i: number) => {
    const el = box.current?.querySelector<HTMLElement>(`[data-line="${i}"]`);
    if (el && box.current) box.current.scrollTop = el.offsetTop - box.current.clientHeight / 3;
  };

  const playFrom = (sec: number) => {
    if (audio.current) {
      audio.current.currentTime = sec;
      audio.current.play();
      return;
    }
    seekTo.current = sec;
    load().catch(() => undefined);
  };

  useEffect(() => {
    if (playing && active >= 0) scrollToLine(active);
  }, [active, playing]);

  useEffect(() => {
    if (audio.current) audio.current.playbackRate = speed;
  }, [speed, src]);

  useEffect(() => {
    if (!focus) return;
    let i = -1;
    if (focus.time) {
      const sec = toSec(focus.time);
      starts.forEach((s, idx) => {
        if (s <= sec) i = idx;
      });
      playFrom(sec);
    } else {
      i = findQuote(turns, focus.quote || "");
    }
    if (i < 0) return;
    scrollToLine(i);
    setFlash(i);
    const t = setTimeout(() => setFlash(-1), 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, turns]);

  const hasLines = turns.length > 0;
  return (
    <div>
      <div
        style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 8, padding: 10, marginBottom: 10 }}
      >
        {src ? (
          <div>
            <audio
              ref={audio}
              controls
              autoPlay
              src={src}
              style={{ width: "100%", height: 36 }}
              onLoadedMetadata={(e) => {
                e.currentTarget.playbackRate = speed;
                if (seekTo.current != null) e.currentTarget.currentTime = seekTo.current;
                seekTo.current = null;
              }}
              onTimeUpdate={(e) => setNow(e.currentTarget.currentTime)}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
            />
            <Space size={6} wrap style={{ marginTop: 6 }}>
              <Button size="small" onClick={() => audio.current && (audio.current.currentTime -= 10)}>
                « 10s
              </Button>
              <Button size="small" onClick={() => audio.current && (audio.current.currentTime += 10)}>
                10s »
              </Button>
              <Segmented
                size="small"
                value={speed}
                onChange={(v) => setSpeed(Number(v))}
                options={SPEEDS.map((s) => ({ label: `${s}×`, value: s }))}
              />
            </Space>
          </div>
        ) : (
          <Space wrap>
            <Button
              type="primary"
              loading={state === "loading"}
              onClick={() => load().catch(() => undefined)}
              disabled={!callId}
            >
              ▶ Play recording
            </Button>
            {state === "error" ? (
              <RecordingError fallbackUrl={fallbackUrl} />
            ) : (
              hasLines && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  or click any time in the transcript
                </Text>
              )
            )}
          </Space>
        )}
        {share && (
          <div style={{ marginTop: 10 }}>
            <TalkRatio share={share} legend={sides.legend} known={sides.known} />
          </div>
        )}
      </div>

      {!hasLines ? (
        <Paragraph style={{ whiteSpace: "pre-wrap", maxHeight, overflow: "auto" }}>
          {raw || "No transcript available."}
        </Paragraph>
      ) : (
        <>
          <Space style={{ marginBottom: 6 }} wrap>
            <Input.Search
              size="small"
              allowClear
              placeholder="Search transcript"
              style={{ width: 220 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search.trim().length > 1 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {matches} {matches === 1 ? "line" : "lines"}
              </Text>
            )}
            {keywords.length > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                <mark style={{ background: "#ffccc7", padding: "0 2px" }}>flagged words</mark> marked
              </Text>
            )}
          </Space>
          <div ref={box} style={{ maxHeight, overflow: "auto", padding: "4px 2px", position: "relative" }}>
            {turns.map((l, i) => {
              const st = SIDE_STYLE[sides.sideOf(l.speaker)];
              const sameAsPrev = i > 0 && turns[i - 1].speaker === l.speaker;
              const isActive = i === active;
              const dim = search.trim().length > 1 && !norm(l.text).includes(norm(search));
              return (
                <div
                  key={i}
                  data-line={i}
                  style={{
                    display: "flex",
                    justifyContent: st.justify,
                    marginTop: sameAsPrev ? 2 : 8,
                    opacity: dim ? 0.45 : 1,
                  }}
                >
                  <div
                    style={{
                      maxWidth: "78%",
                      padding: "6px 10px",
                      borderRadius: 10,
                      background: st.bg,
                      border: `1px solid ${isActive || i === flash ? st.name : st.border}`,
                      boxShadow: isActive || i === flash ? `0 0 0 2px ${st.border}` : undefined,
                      transition: "box-shadow .2s, border-color .2s",
                    }}
                  >
                    <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                      {!sameAsPrev && <span style={{ fontWeight: 600, color: st.name }}>{l.speaker} · </span>}
                      <Button
                        type="link"
                        size="small"
                        onClick={() => playFrom(starts[i])}
                        title="Play from here"
                        style={{ padding: 0, height: "auto", fontSize: 11, color: "#8c8c8c" }}
                      >
                        ▶ {l.time}
                      </Button>
                    </div>
                    <div>
                      <Highlighted
                        text={l.text}
                        search={search}
                        keywords={sides.sideOf(l.speaker) === 1 || !sides.known ? keywords : []}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
// Summary on the left, recording + transcript on the right. Loads the call on mount.
export const CallDetailPanel = ({ callId }: { callId: number }) => {
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setDetail(null);
    fetchCallDetail(callId).then((res) => {
      if (!live) return;
      setDetail(res);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [callId]);

  if (loading) return <Spin size="small" />;
  if (!detail) return <Text type="secondary">Could not load this call.</Text>;

  const section = (title: string, body: any) => (
    <div style={{ marginBottom: 14 }}>
      <Text type="secondary" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {title}
      </Text>
      <div style={{ marginTop: 4 }}>{body}</div>
    </div>
  );
  return (
    <div>
      <Space wrap size={[6, 6]} style={{ marginBottom: 12 }}>
        <Text strong>{detail.customer_name || detail.phone_number || "Unknown customer"}</Text>
        <Tag>{fmtDT(detail.call_start_time)}</Tag>
        <Tag>{direction(detail.call_type)}</Tag>
        <Tag>{dur(detail.duration)}</Tag>
        {detail.agent_name && <Tag color="blue">{detail.agent_name}</Tag>}
        {detail.team && <Tag>{detail.team}</Tag>}
      </Space>
      <Row gutter={20}>
        <Col xs={24} md={10}>
          {section(
            "Customer sentiment",
            <div>
              {sentimentTag(detail.llm_sentiment_label)}
              {detail.severity && (
                <Tag color={detail.severity === "high" ? "red" : detail.severity === "medium" ? "orange" : "green"}>
                  {detail.severity} severity
                </Tag>
              )}
              {detail.sentiment_reasoning && (
                <Paragraph type="secondary" style={{ fontSize: 12, margin: "4px 0 0" }}>
                  {detail.sentiment_reasoning}
                </Paragraph>
              )}
              {detail.matched_keywords?.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  {detail.matched_keywords.map((k: string) => (
                    <Tag key={k} color="magenta">
                      {k}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
          )}
          {section(
            "Summary",
            <Paragraph style={{ margin: 0 }}>{detail.call_summary || "No summary for this call."}</Paragraph>
          )}
          {detail.qc &&
            section(
              "QC audit",
              <div>
                <Tag color={QC_FLAG_COLOR[detail.qc.flag]}>{(detail.qc.flag || "").toUpperCase()}</Tag>
                {detail.qc.average_score != null && (
                  <Text type="secondary">avg {detail.qc.average_score.toFixed(1)}</Text>
                )}
                <Paragraph type="secondary" style={{ fontSize: 12, margin: "4px 0 0" }}>
                  {detail.qc.summary}
                </Paragraph>
              </div>
            )}
        </Col>
        <Col xs={24} md={14}>
          {section(
            "Recording & transcript",
            <CallPlayback
              callId={detail.id}
              fallbackUrl={detail.myoperator_recording_url || undefined}
              lines={detail.transcript_lines}
              raw={detail.transcript}
              duration={detail.duration}
              keywords={detail.matched_keywords || []}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};
